import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  collection, getDocs, getDoc, doc, query, where, orderBy, limit,
} from 'firebase/firestore';
import { db, formatCurrency } from '../../services/firebase';

const ADMIN_UID = 'MoV9MPPSGgTE1RDnq0QCYjBpvTy2';

// Returns week ID for a date offset by N weeks from today
const getWeekId = (weeksAgo = 0) => {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth(), now.getDate() - weeksAgo * 7);
  const startOfYear = new Date(target.getFullYear(), 0, 1);
  const weekNum = Math.ceil(
    ((target - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7
  );
  return `${target.getFullYear()}-${String(weekNum).padStart(2, '0')}`;
};

const weekLabel = (weeksAgo) => {
  if (weeksAgo === 0) return 'This week';
  if (weeksAgo === 1) return 'Last week';
  return `${weeksAgo} weeks ago`;
};

const AdminDashboard = () => {
  const { authUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [families, setFamilies] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [familyDetails, setFamilyDetails] = useState([]);

  // Guard — redirect non-admins
  useEffect(() => {
    if (authUser && authUser.uid !== ADMIN_UID) {
      navigate('/');
    }
  }, [authUser, navigate]);

  useEffect(() => {
    if (!authUser || authUser.uid !== ADMIN_UID) return;
    loadData();
  }, [authUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all families
      const familiesSnap = await getDocs(collection(db, 'families'));
      const allFamilies = familiesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setFamilies(allFamilies);

      const activeFamilies = allFamilies.filter(f => f.inviteAccepted);

      // Build 4-week trend
      const weekIds = [0, 1, 2, 3].map(w => ({ weeksAgo: w, id: getWeekId(w) }));
      const stats = [];

      for (const { weeksAgo, id } of weekIds) {
        let activeCount = 0;
        let closedCount = 0;
        let totalSpent = 0;
        let totalSaved = 0;
        let totalPlannedFamilies = 0;

        for (const family of activeFamilies) {
          // Check for transactions this week
          const txSnap = await getDocs(
            query(
              collection(db, 'families', family.id, 'transactions'),
              where('weekId', '==', id),
              limit(1)
            )
          );
          if (!txSnap.empty) activeCount++;

          // Check week doc directly
          const weekRef = doc(db, 'families', family.id, 'weeks', id);
          const weekDocSnap = await getDoc(weekRef);

          if (weekDocSnap.exists()) {
            const week = weekDocSnap.data();
            if (week.closedOut) closedCount++;
            totalSpent += week.spent || 0;
            totalSaved += week.saved || 0;
            if ((week.plannedSpend || 0) > 0) totalPlannedFamilies++;
          }
        }

        stats.push({
          weeksAgo,
          weekId: id,
          label: weekLabel(weeksAgo),
          totalFamilies: activeFamilies.length,
          activeCount,
          closedCount,
          totalSpent,
          totalSaved,
          totalPlannedFamilies,
          activeRate: activeFamilies.length > 0
            ? Math.round((activeCount / activeFamilies.length) * 100) : 0,
          closeOutRate: activeFamilies.length > 0 && weeksAgo > 0
            ? Math.round((closedCount / activeFamilies.length) * 100) : null,
        });
      }

      setWeeklyStats(stats);

      // Build family-level detail
      const details = [];
      for (const family of activeFamilies) {
        const studentFirst = family.studentName?.split(' ')[0] || '—';

        // Count total weeks with activity
        const allWeekSnap = await getDocs(
          collection(db, 'families', family.id, 'weeks')
        );
        const weekDocs = allWeekSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(w => w.id <= getWeekId(0)); // exclude future weeks

        const weeksActive = weekDocs.length;
        const weeksClosed = weekDocs.filter(w => w.closedOut).length;
        const totalSaved = weekDocs.reduce((s, w) => s + (w.saved || 0), 0);
        const totalSpent = weekDocs.reduce((s, w) => s + (w.spent || 0), 0);

        // Last activity
        const latestTxSnap = await getDocs(
          query(
            collection(db, 'families', family.id, 'transactions'),
            orderBy('createdAt', 'desc'),
            limit(1)
          )
        );
        let lastActive = '—';
        if (!latestTxSnap.empty) {
          const ts = latestTxSnap.docs[0].data().createdAt;
          if (ts?.toDate) lastActive = ts.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }

        // Current week activity
        const currentWeekTxSnap = await getDocs(
          query(
            collection(db, 'families', family.id, 'transactions'),
            where('weekId', '==', getWeekId(0)),
            limit(1)
          )
        );
        const activeThisWeek = !currentWeekTxSnap.empty;

        details.push({
          id: family.id,
          studentFirst,
          allowance: family.allowance?.amount || 0,
          matchEnabled: family.match?.enabled || false,
          walletStarted: family.walletStarted !== false,
          weeksActive,
          weeksClosed,
          closeOutRate: weeksActive > 0 ? Math.round((weeksClosed / weeksActive) * 100) : 0,
          totalSaved,
          totalSpent,
          lastActive,
          activeThisWeek,
          createdAt: family.createdAt?.toDate
            ? family.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '—',
        });
      }

      // Sort by most recently active
      details.sort((a, b) => (b.activeThisWeek ? 1 : 0) - (a.activeThisWeek ? 1 : 0));
      setFamilyDetails(details);

    } catch (err) {
      console.error('Admin load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!authUser || authUser.uid !== ADMIN_UID) return null;

  if (loading) {
    return (
      <div className="screen screen--no-nav" style={{ display: 'flex',
        alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  const currentStats = weeklyStats[0];
  const enrolled = families.filter(f => f.inviteAccepted).length;
  const registered = families.length;

  return (
    <div className="screen screen--no-nav" style={{ paddingBottom: 40 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 24 }}>
        <h1 className="page-title">Admin</h1>
        <button onClick={loadData} style={{
          background: 'none', border: '1px solid var(--color-border)',
          borderRadius: 8, padding: '6px 12px', fontSize: '0.8125rem',
          cursor: 'pointer', fontFamily: 'var(--font-body)',
          color: 'var(--color-text-2)',
        }}>
          Refresh
        </button>
      </div>

      {/* Summary stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 24 }}>
        <StatCard label="Families registered" value={registered} />
        <StatCard label="Students connected" value={enrolled} />
        <StatCard label="Active this week" value={currentStats?.activeCount || 0}
          sub={`${currentStats?.activeRate || 0}% of enrolled`} />
        <StatCard label="Close-out rate" value={`${weeklyStats[1]?.closeOutRate ?? '—'}%`}
          sub="last week" />
        <StatCard label="Avg weekly savings"
          value={formatCurrency(enrolled > 0 && currentStats
            ? currentStats.totalSaved / Math.max(currentStats.activeCount, 1) : 0)} />
        <StatCard label="Planning feature"
          value={`${currentStats?.totalPlannedFamilies || 0}`}
          sub="families used it this week" />
      </div>

      {/* 4-week trend */}
      <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>4-week trend</h2>
      <div className="card" style={{ padding: 0, marginBottom: 24, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-surface-2)' }}>
              {['Week', 'Active', 'Closed out', 'Spent', 'Saved'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left',
                  fontWeight: 600, color: 'var(--color-text-2)',
                  borderBottom: '1px solid var(--color-border)' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeklyStats.map((s, i) => (
              <tr key={s.weekId} style={{
                borderBottom: i < weeklyStats.length - 1 ? '1px solid var(--color-border)' : 'none',
              }}>
                <td style={{ padding: '10px 12px', color: 'var(--color-text-2)' }}>{s.label}</td>
                <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                  {s.activeCount}/{s.totalFamilies}
                  <span style={{ fontWeight: 400, color: 'var(--color-text-3)',
                    fontSize: '0.75rem' }}> ({s.activeRate}%)</span>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  {s.weeksAgo === 0 ? '—' : s.closeOutRate !== null
                    ? `${s.closeOutRate}%` : '—'}
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--color-accent)' }}>
                  {formatCurrency(s.totalSpent)}
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--color-primary)' }}>
                  {formatCurrency(s.totalSaved)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Family detail */}
      <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>Families</h2>
      <div className="stack stack-3">
        {familyDetails.map(f => (
          <div key={f.id} className="card" style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9375rem', display: 'flex',
                  alignItems: 'center', gap: 8 }}>
                  {f.studentFirst}
                  {f.activeThisWeek && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 700,
                      background: 'var(--color-primary-pale)', color: 'var(--color-primary)',
                      padding: '2px 7px', borderRadius: 'var(--radius-full)' }}>
                      Active
                    </span>
                  )}
                  {!f.walletStarted && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 700,
                      background: 'var(--color-accent-light)', color: '#C45E1A',
                      padding: '2px 7px', borderRadius: 'var(--radius-full)' }}>
                      Awaiting Sunday
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 2 }}>
                  {formatCurrency(f.allowance)}/wk · joined {f.createdAt}
                </div>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', textAlign: 'right' }}>
                Last active<br />
                <span style={{ fontWeight: 600, color: 'var(--color-text-2)' }}>{f.lastActive}</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              <MiniMetric label="Weeks" value={f.weeksActive} />
              <MiniMetric label="Closed" value={`${f.closeOutRate}%`} />
              <MiniMetric label="Saved" value={formatCurrency(f.totalSaved)} color="var(--color-primary)" />
              <MiniMetric label="Spent" value={formatCurrency(f.totalSpent)} color="var(--color-accent)" />
            </div>
          </div>
        ))}
        {familyDetails.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: 40 }}>
            <p style={{ color: 'var(--color-text-3)' }}>No enrolled families yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub }) => (
  <div className="card" style={{ textAlign: 'center', padding: '14px 10px' }}>
    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem',
      fontWeight: 700, color: 'var(--color-text)' }}>{value}</div>
    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-3)', marginTop: 4 }}>{label}</div>
    {sub && <div style={{ fontSize: '0.65rem', color: 'var(--color-text-3)', marginTop: 2 }}>{sub}</div>}
  </div>
);

const MiniMetric = ({ label, value, color }) => (
  <div style={{ background: 'var(--color-surface-2)', borderRadius: 8,
    padding: '8px 10px', textAlign: 'center' }}>
    <div style={{ fontSize: '0.8125rem', fontWeight: 700,
      color: color || 'var(--color-text)' }}>{value}</div>
    <div style={{ fontSize: '0.65rem', color: 'var(--color-text-3)', marginTop: 2 }}>{label}</div>
  </div>
);

export default AdminDashboard;
